import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { InternationalizationService } from '@soff/shared-ui';
import { NotificationActions, NotificationReducer } from 'src/app/shared/state/notification';
import { TscAdminActions } from '.';
import { TscAdminService } from '../services/admin.service';

@Injectable()
export class TscAdminRegionEffects {
  private actions$ = inject(Actions);
  private api = inject(TscAdminService);
  private i18n = inject(InternationalizationService);
  private notifications = inject<Store<NotificationReducer.State>>(Store);

  getRegionPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.GET_REGIONS),
      map((action: TscAdminActions.GetRegions) => action.payload),
      switchMap(payload =>
        this.api.loadRegions(payload).pipe(
          map(response => (response.error ? new TscAdminActions.GetRegionsFailure(response.error) : new TscAdminActions.GetRegionsSuccess(response))),
          catchError(error => of(new TscAdminActions.GetRegionsFailure(error)))
        )
      )
    )
  );

  createRegion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.CREATE_REGION),
      map((action: TscAdminActions.CreateRegion) => action.payload),
      switchMap(payload =>
        this.api.createRegion(payload).pipe(
          map(response =>
            response.error
              ? new TscAdminActions.CreateRegionFailure(response.error, payload)
              : new TscAdminActions.CreateRegionSuccess(response, payload)
          ),
          catchError(error => of(new TscAdminActions.CreateRegionFailure(error, payload)))
        )
      )
    )
  );

  createRegionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.CREATE_REGION_SUCCESS),
        map((action: TscAdminActions.CreateRegionSuccess) => action.response),
        tap(() => {
          const snackbarMessage = {
            success: true,
            message: this.i18n.getTranslation('tscAdmin.regions.snackbars.createSuccess.message', null),
          };
          this.notifications.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  createRegionFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.CREATE_REGION_FAILURE),
        map((action: TscAdminActions.CreateRegionFailure) => action.error),
        tap(error => {
          const snackbarMessage = {
            success: false,
            message: this.i18n.getTranslation('tscAdmin.regions.snackbars.createError.message', { error }),
          };
          this.notifications.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  updateRegion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.UPDATE_REGION),
      map((action: TscAdminActions.UpdateRegion) => action.payload),
      switchMap(payload =>
        this.api.updateRegion(payload).pipe(
          map(response =>
            response.error
              ? new TscAdminActions.UpdateRegionFailure(response.error, payload)
              : new TscAdminActions.UpdateRegionSuccess(response, payload)
          ),
          catchError(error => of(new TscAdminActions.UpdateRegionFailure(error, payload)))
        )
      )
    )
  );

  updateRegionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.UPDATE_REGION_SUCCESS),
        map((action: TscAdminActions.UpdateRegionSuccess) => action.response),
        tap(() => {
          const snackbarMessage = {
            success: true,
            message: this.i18n.getTranslation('tscAdmin.regions.snackbars.updateSuccess.message', null),
          };
          this.notifications.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  updateRegionFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.UPDATE_REGION_FAILURE),
        map((action: TscAdminActions.UpdateRegionFailure) => action.error),
        tap(error => {
          const snackbarMessage = {
            success: false,
            message: this.i18n.getTranslation('tscAdmin.regions.snackbars.updateError.message', { error }),
          };
          this.notifications.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  getVkorgPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.GET_VKORGS),
      map((action: TscAdminActions.GetVkorgs) => action.payload),
      switchMap(payload =>
        this.api.loadVkorgs(payload).pipe(
          map(response => (response.error ? new TscAdminActions.GetVkorgsFailure(response.error) : new TscAdminActions.GetVkorgsSuccess(response))),
          catchError(error => of(new TscAdminActions.GetVkorgsFailure(error)))
        )
      )
    )
  );

  createVkorg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.CREATE_VKORG),
      map((action: TscAdminActions.CreateVkorg) => action.payload),
      switchMap(payload =>
        this.api.createVkorg(payload).pipe(
          map(response =>
            response.error
              ? new TscAdminActions.CreateVkorgFailure(response.error, payload)
              : new TscAdminActions.CreateVkorgSuccess(response, payload)
          ),
          catchError(error => of(new TscAdminActions.CreateVkorgFailure(error, payload)))
        )
      )
    )
  );

  createVkorgSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.CREATE_VKORG_SUCCESS),
        map((action: TscAdminActions.CreateVkorgSuccess) => action.response),
        tap(() => {
          const snackbarMessage = {
            success: true,
            message: this.i18n.getTranslation('tscAdmin.vkorgs.snackbars.createSuccess.message', null),
          };
          this.notifications.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  createVkorgFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.CREATE_VKORG_FAILURE),
        map((action: TscAdminActions.CreateVkorgFailure) => action.error),
        tap(error => {
          const snackbarMessage = {
            success: false,
            message: this.i18n.getTranslation('tscAdmin.vkorgs.snackbars.createError.message', { error }),
          };
          this.notifications.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  updateVkorg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TscAdminActions.UPDATE_VKORG),
      map((action: TscAdminActions.UpdateVkorg) => action.payload),
      switchMap(payload =>
        this.api.updateVkorg(payload).pipe(
          map(response =>
            response.error
              ? new TscAdminActions.UpdateVkorgFailure(response.error, payload)
              : new TscAdminActions.UpdateVkorgSuccess(response, payload)
          ),
          catchError(error => of(new TscAdminActions.UpdateVkorgFailure(error, payload)))
        )
      )
    )
  );

  updateVkorgSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.UPDATE_VKORG_SUCCESS),
        map((action: TscAdminActions.UpdateVkorgSuccess) => action.response),
        tap(() => {
          const snackbarMessage = {
            success: true,
            message: this.i18n.getTranslation('tscAdmin.vkorgs.snackbars.updateSuccess.message', null),
          };
          this.notifications.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );

  updateVkorgFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TscAdminActions.UPDATE_VKORG_FAILURE),
        map((action: TscAdminActions.UpdateVkorgFailure) => action.error),
        tap(error => {
          const snackbarMessage = {
            success: false,
            message: this.i18n.getTranslation('tscAdmin.vkorgs.snackbars.updateError.message', { error }),
          };
          this.notifications.dispatch(new NotificationActions.ShowSnackbar(snackbarMessage));
        })
      ),
    { dispatch: false }
  );
}
